// ── the skill document class ────────────────────────────────────────────────
// The class itself: what a skill IS, independent of where it is stored. Kept
// free of node built-ins so the reading surfaces (client components) can import
// the type and the heading law without dragging `node:fs` into a browser chunk.
// The loader lives next door in ./index.ts and is server-only.
//
// A skill is an ordinary document wearing a modifier. Same markdown, same prose
// — but it carries a MANIFEST declaring who kept it, what it makes you able to
// do, and when to load it. That manifest is the whole difference: handing
// someone the link tells them more than handing them the words would.

/** the modifier that makes a document a skill. */
export const SKILL_KIND = "skill" as const;

export interface SkillManifest {
  /** stable slug — half the link, never changes once shared. */
  name: string;
  /** human title; falls back to the name. */
  title: string;
  /** the modifier itself. */
  kind: typeof SKILL_KIND;
  /** what this makes a reader ABLE TO DO — written for a model deciding whether to load it. */
  description: string;
  /** the trigger: when to reach for it. The highest-value diagnostic field. */
  when_to_load?: string;
  version?: string;
  /** ISO date of the last substantive revision. */
  updated?: string;
  authored_by?: string;
  author_url?: string;
  /** for a skill ingested from elsewhere on the web: the folder it came from
   *  (where the card's host link and the ledger's `source` row open). */
  source_url?: string;
  /** the raw markdown it was ingested from — the keeper's latest, verbatim. */
  source_raw?: string;
  license?: string;
  /** who brought it to the commons, when that is not who wrote it — the second
   *  attribution. A skill is its author's; the person who carried it here is
   *  named beside them, never instead of them. */
  brought_by?: string;
  tags: string[];
  /** where the skill files on a public index (design · code · writing · …);
   *  inferred from tags when absent. */
  category?: string;
}

/** one kept state of a skill — the current one, or one it superseded. */
export interface SkillVersion {
  /** stable handle: the version string for a file skill, a row id for a member's */
  id: string;
  version?: string;
  updated?: string;
  /** true for the state the link opens by default */
  current: boolean;
}

export interface Skill extends SkillManifest {
  /** the markdown body, manifest stripped. */
  body: string;
  /** the manifest as it was authored — what the raw endpoint hands back verbatim. */
  raw: string;
  /** rough read time in minutes, for the diagnostic header. */
  minutes: number;
  /** headings (## level) — the page's contents rail. */
  outline: { id: string; text: string }[];
}

/** heading → anchor id. Shared by the outline and the rendered headings, so a
 *  contents entry and its heading can never disagree. */
export function headingId(text: string): string {
  return (
    text
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "") || "section"
  );
}

/** the canonical, shareable link for a skill — the interface to the whole class. */
export function skillHref(name: string): string {
  return `/skills/${name}`;
}
